import { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import YouTubeAnalyzer from './YouTubeAnalyzer';
import LandingPage from './LandingPage';
import SignInPage from './SignInPage';
import PricingPage from './PricingPage';
import PaymentPage from './PaymentPage';
import HistoryPage from './HistoryPage';
import { BlogPage, ArticlePage } from './BlogPage';
import CompetitorPage from './CompetitorPage';
import SimulatorPage from './SimulatorPage';
import FeaturesPage from './FeaturesPage';
import { AuthProvider, useAuth } from './AuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');

const buildUrl = (endpoint) => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${path}`;
};

const SIDEBAR_WIDTH = 240;

const APP_CSS = `
@media (max-width: 768px) {
  .titler-sidebar { transform: translateX(-100%); position: fixed !important; z-index: 200 !important; }
  .titler-sidebar.open { transform: translateX(0); }
  .titler-sidebar-overlay.open { display: block !important; }
  .titler-mobile-topbar { display: flex !important; }
  .titler-main-content { margin-left: 0 !important; padding-top: 56px !important; }
}
@media (min-width: 769px) {
  .titler-sidebar-overlay { display: none !important; }
  .titler-mobile-topbar { display: none !important; }
}
/* Guest top navbar mobile */
@media (max-width: 640px) {
  .titler-nav-desktop { display: none !important; }
  .titler-hamburger { display: flex !important; }
}
@media (min-width: 641px) {
  .titler-nav-desktop { display: flex !important; }
  .titler-hamburger { display: none !important; }
  .titler-mobile-menu { display: none !important; }
}
`;

// --- Icons ---
const AnalyzeIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);
const HistoryIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const BlogIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);
const CompetitorIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/>
  </svg>
);
const SimulatorIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);
const SignOutIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

// --- Guest top navbar styles ---
const headerStyles = {
  header: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 16px', height: '56px',
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  logo: {
    fontSize: '22px', fontWeight: '800', letterSpacing: '-1px',
    background: 'linear-gradient(135deg, #a78bfa, #818cf8, #6366f1)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    cursor: 'pointer', userSelect: 'none', flexShrink: 0,
  },
  nav: { display: 'flex', alignItems: 'center', gap: '4px' },
  navLink: {
    padding: '6px 14px', borderRadius: '8px', border: 'none',
    background: 'transparent', color: '#94a3b8', fontSize: '14px',
    fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'color 0.15s, background 0.15s', whiteSpace: 'nowrap',
  },
  navLinkActive: {
    padding: '6px 14px', borderRadius: '8px', border: 'none',
    background: 'rgba(124,58,237,0.15)', color: '#c4b5fd', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  signInBtn: {
    padding: '6px 18px', borderRadius: '8px',
    border: '1px solid rgba(124,58,237,0.4)',
    background: 'rgba(124,58,237,0.12)', color: '#c4b5fd', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
    marginLeft: '8px', transition: 'background 0.15s', whiteSpace: 'nowrap',
  },
  hamburger: {
    display: 'none', alignItems: 'center', justifyContent: 'center',
    width: '36px', height: '36px', borderRadius: '8px', border: 'none',
    background: 'transparent', color: '#cbd5e1', cursor: 'pointer',
    padding: 0, flexShrink: 0,
  },
  mobileMenu: {
    position: 'fixed', top: '56px', left: 0, right: 0, zIndex: 99,
    background: 'rgba(15, 23, 42, 0.95)',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '8px 16px 16px',
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
  mobileNavLink: {
    padding: '10px 16px', borderRadius: '8px', border: 'none',
    background: 'transparent', color: '#94a3b8', fontSize: '15px',
    fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
    textAlign: 'left', width: '100%',
  },
  mobileNavLinkActive: {
    padding: '10px 16px', borderRadius: '8px', border: 'none',
    background: 'rgba(124,58,237,0.15)', color: '#c4b5fd', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
    textAlign: 'left', width: '100%',
  },
};

// --- Sidebar styles ---
const sidebarStyles = {
  sidebar: {
    position: 'fixed', top: 0, left: 0, bottom: 0,
    width: `${SIDEBAR_WIDTH}px`,
    background: '#0b0f1a',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', flexDirection: 'column',
    zIndex: 150,
    transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  logoArea: {
    padding: '20px 20px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  logo: {
    fontSize: '22px', fontWeight: '800', letterSpacing: '-1px',
    background: 'linear-gradient(135deg, #a78bfa, #818cf8, #6366f1)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    userSelect: 'none',
  },
  navSection: {
    flex: 1, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: '4px',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 14px', borderRadius: '10px', border: 'none',
    background: 'transparent', color: '#94a3b8', fontSize: '14px',
    fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'background 0.15s, color 0.15s',
    textAlign: 'left', width: '100%',
  },
  navItemActive: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 14px', borderRadius: '10px', border: 'none',
    background: 'rgba(124,58,237,0.15)', color: '#c4b5fd', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
    textAlign: 'left', width: '100%',
  },
  bottomSection: {
    padding: '12px', borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
  userInfo: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 14px', marginBottom: '4px',
  },
  avatar: {
    width: '32px', height: '32px', borderRadius: '50%',
    border: '2px solid rgba(124,58,237,0.4)', flexShrink: 0,
  },
  userName: {
    fontSize: '13px', color: '#cbd5e1', fontWeight: '500',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  userEmail: {
    fontSize: '11px', color: '#475569',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  proBadge: {
    padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '700',
    background: 'rgba(124,58,237,0.2)', color: '#a78bfa', letterSpacing: '0.5px',
    marginLeft: 'auto', flexShrink: 0,
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 140, display: 'none',
  },
  mobileTopbar: {
    position: 'fixed', top: 0, left: 0, right: 0, height: '56px',
    display: 'none', alignItems: 'center', padding: '0 16px',
    background: 'rgba(15, 23, 42, 0.95)', zIndex: 100,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
};

const GUEST_NAV = [
  { key: 'landing', label: 'Home', hash: '#/' },
  { key: 'features', label: 'Features', hash: '#/features' },
  { key: 'app', label: 'Analyze', hash: '#/app' },
  { key: 'pricing', label: 'Pricing', hash: '#/pricing' },
  { key: 'blog', label: 'Blog', hash: '#/blog' },
];

const getPageFromHash = () => {
  const hash = window.location.hash;
  if (hash.includes('access_token=')) return 'signin'; // OAuth redirect in progress
  if (hash === '#/features') return 'features';
  if (hash === '#/app') return 'app';
  if (hash === '#/history') return 'history';
  if (hash === '#/competitors') return 'competitors';
  if (hash === '#/simulator') return 'simulator';
  if (hash === '#/signin') return 'signin';
  if (hash === '#/pricing') return 'pricing';
  if (hash === '#/payment') return 'payment';
  if (hash === '#/settings') return 'settings';
  if (hash === '#/blog') return 'blog';
  if (hash.startsWith('#/blog/')) return 'article';
  return 'landing';
};

const getArticleSlug = () => {
  const hash = window.location.hash;
  if (hash.startsWith('#/blog/')) return hash.slice(7);
  return null;
};

const parseOAuthFromHash = () => {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token=')) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const accessToken = params.get('access_token');
  if (!accessToken) return null;
  return { access_token: accessToken };
};

const AppInner = () => {
  const [page, setPage] = useState(getPageFromHash);
  const [showSignedOut, setShowSignedOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(() => !!parseOAuthFromHash());
  const [oauthError, setOauthError] = useState(null);
  const { user, login, logout, updateUser, authReady } = useAuth();
  const menuRef = useRef(null);
  const [creditToast, setCreditToast] = useState(null);
  const creditToastTimer = useRef(null);

  const showCreditToast = useCallback((amount = 1) => {
    if (creditToastTimer.current) clearTimeout(creditToastTimer.current);
    setCreditToast(amount);
    creditToastTimer.current = setTimeout(() => {
      setCreditToast(null);
      creditToastTimer.current = null;
    }, 2000);
  }, []);

  // Handle Google OAuth redirect callback
  useEffect(() => {
    const oauthData = parseOAuthFromHash();
    if (!oauthData || user) return;
    setOauthLoading(true);

    (async () => {
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${oauthData.access_token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch Google profile');
        const profile = await res.json();

        let tier = 'free';
        let requestLimit = 0;
        let regData = null;
        const regRes = await fetch(`${API_BASE_URL}/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${oauthData.access_token}`,
          },
        });
        if (!regRes.ok) throw new Error('Failed to register account. Please try again.');
        regData = await regRes.json();
        tier = regData.tier || 'free';
        requestLimit = regData.requestLimit ?? 0;

        login({
          credential: 'authenticated',
          name: profile.name,
          email: profile.email,
          picture: profile.picture,
          isPro: regData?.isPro ?? (tier === 'pro'),
          tier,
          requestLimit,
          isNewUser: regData?.isNewUser || false,
        });
        window.location.hash = '#/app';
      } catch (err) {
        console.error('Failed to complete sign-in:', err);
        setOauthError(err.message || 'Something went wrong. Please try again.');
      } finally {
        setOauthLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onHash = () => { setPage(getPageFromHash()); setMenuOpen(false); setSidebarOpen(false); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Scroll to top on page change — multiple attempts to defeat Chrome mobile scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    scrollToTop();
    // Chrome mobile sometimes restores scroll after paint — catch it
    const t1 = setTimeout(scrollToTop, 0);
    const t2 = setTimeout(scrollToTop, 50);
    const t3 = requestAnimationFrame(scrollToTop);
    return () => { clearTimeout(t1); clearTimeout(t2); cancelAnimationFrame(t3); };
  }, [page]);

  // Show welcome banner for new users, then clear the flag
  useEffect(() => {
    if (user?.isNewUser) {
      setShowWelcome(true);
      updateUser({ isNewUser: false });
    }
  }, [user?.isNewUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // When user logs in, redirect to app if on guest-only pages
  useEffect(() => {
    if (user && (page === 'landing' || page === 'signin' || page === 'pricing')) {
      window.location.hash = '#/app';
    }
  }, [user, page]);

  // Close mobile menu on outside click (guest mode)
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const navigate = (hash) => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); window.location.hash = hash; setMenuOpen(false); setSidebarOpen(false); };
  const goToApp = () => navigate('#/app');
  const goSignIn = () => navigate('#/signin');
  const goPayment = () => navigate('#/payment');

  const handleProStart = () => {
    if (user) { goPayment(); } else { goSignIn(); }
  };

  const handleSignOut = () => {
    logout();
    setShowSignedOut(true);
    setSidebarOpen(false);
    window.location.hash = '#/signin';
  };

  // ========== AUTH NOT READY ==========
  if (!authReady && !oauthLoading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        flexDirection: 'column', gap: '16px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        <div style={{
          width: '36px', height: '36px', border: '3px solid rgba(124,58,237,0.2)',
          borderTopColor: '#7c3aed', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ========== OAUTH LOADING / ERROR ==========
  if (oauthLoading || oauthError) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        flexDirection: 'column', gap: '16px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        {oauthLoading ? (
          <>
            <div style={{
              width: '36px', height: '36px', border: '3px solid rgba(124,58,237,0.2)',
              borderTopColor: '#7c3aed', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <span style={{ color: '#94a3b8', fontSize: '14px' }}>Signing you in...</span>
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <span style={{ color: '#f87171', fontSize: '16px', fontWeight: '600' }}>Sign-in failed</span>
            <span style={{ color: '#94a3b8', fontSize: '14px', textAlign: 'center', maxWidth: '320px' }}>{oauthError}</span>
            <button
              onClick={() => { setOauthError(null); window.location.hash = '#/signin'; setPage('signin'); }}
              style={{
                marginTop: '8px', padding: '10px 24px', borderRadius: '10px',
                border: '1px solid rgba(124,58,237,0.4)', background: 'rgba(124,58,237,0.12)',
                color: '#c4b5fd', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
          </>
        )}
      </div>
    );
  }

  // ========== LOGGED-IN LAYOUT (Sidebar) ==========
  if (user) {
    return (
      <div style={{ minHeight: '100vh', background: '#000' }}>
        <style>{APP_CSS}</style>

        {/* Mobile top bar */}
        <div className="titler-mobile-topbar" style={sidebarStyles.mobileTopbar}>
          <button
            onClick={() => setSidebarOpen(o => !o)}
            style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: '8px', marginRight: '8px' }}
            aria-label="Menu"
          >
            {sidebarOpen ? (
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            )}
          </button>
          <span style={sidebarStyles.logo}>Titler</span>
        </div>

        {/* Sidebar overlay (mobile) */}
        <div
          className={`titler-sidebar-overlay${sidebarOpen ? ' open' : ''}`}
          style={sidebarStyles.overlay}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Sidebar */}
        <aside className={`titler-sidebar${sidebarOpen ? ' open' : ''}`} style={sidebarStyles.sidebar}>
          <div style={sidebarStyles.logoArea}>
            <span style={sidebarStyles.logo}>Titler</span>
          </div>

          <div style={sidebarStyles.navSection}>
            <button
              onClick={() => navigate('#/app')}
              style={page === 'app' ? sidebarStyles.navItemActive : sidebarStyles.navItem}
            >
              <AnalyzeIcon /> Analyze
            </button>
            <button
              onClick={() => navigate('#/simulator')}
              style={page === 'simulator' ? sidebarStyles.navItemActive : sidebarStyles.navItem}
            >
              <SimulatorIcon /> CTR Simulator
            </button>
            <button
              onClick={() => navigate('#/history')}
              style={page === 'history' ? sidebarStyles.navItemActive : sidebarStyles.navItem}
            >
              <HistoryIcon /> History
            </button>
            <button
              onClick={() => navigate('#/blog')}
              style={(page === 'blog' || page === 'article') ? sidebarStyles.navItemActive : sidebarStyles.navItem}
            >
              <BlogIcon /> Blog
            </button>
          </div>

          <div style={sidebarStyles.bottomSection}>
            {/* User info */}
            <div style={sidebarStyles.userInfo}>
              {user.picture && (
                <img src={user.picture} alt="" style={sidebarStyles.avatar} referrerPolicy="no-referrer" />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={sidebarStyles.userName}>{user.name}</div>
                {user.email && <div style={sidebarStyles.userEmail}>{user.email}</div>}
              </div>
              {user.isPro && <span style={sidebarStyles.proBadge}>PRO</span>}
            </div>

            <button
              onClick={() => navigate('#/settings')}
              style={page === 'settings' ? sidebarStyles.navItemActive : sidebarStyles.navItem}
            >
              <SettingsIcon /> Settings
            </button>
            <button onClick={handleSignOut} style={sidebarStyles.navItem}>
              <SignOutIcon /> Sign out
            </button>
          </div>
        </aside>

        {/* Main content */}
        <div
          className="titler-main-content"
          style={{ marginLeft: `${SIDEBAR_WIDTH}px`, minHeight: '100vh', paddingTop: '0' }}
        >
          {/* Mobile needs top padding for the topbar */}
          <div style={{ paddingTop: 0 }}>
            {!user.isPro && !showWelcome && (
              <div
                onClick={() => navigate('#/settings')}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '10px 16px', cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(99,102,241,0.15))',
                  borderBottom: '1px solid rgba(124,58,237,0.2)',
                  fontSize: '13px', color: '#c4b5fd', fontWeight: '500',
                  textAlign: 'center', lineHeight: '1.4',
                }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="#a78bfa" style={{ flexShrink: 0 }}>
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                </svg>
                <span>Upgrade to <strong style={{ color: '#e2e8f0' }}>Titler Pro</strong> for full AI-powered analysis</span>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#a78bfa" style={{ flexShrink: 0 }}>
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                </svg>
              </div>
            )}
            {showWelcome && (
              <div style={{
                margin: '16px 16px 0', padding: '16px 20px', borderRadius: '12px',
                background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.25)',
                display: 'flex', alignItems: 'flex-start', gap: '12px',
              }}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#818cf8" style={{ flexShrink: 0, marginTop: '2px' }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#e2e8f0', marginBottom: '4px' }}>
                    Welcome to Titler!
                  </div>
                  <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '1.5' }}>
                    Thanks for signing up! We've given you <strong style={{ color: '#a78bfa' }}>3 free credits</strong> to try out all our AI-powered features. Each video analysis uses 1 credit — start by pasting a YouTube URL below.
                  </div>
                </div>
                <button
                  onClick={() => setShowWelcome(false)}
                  style={{
                    background: 'none', border: 'none', color: '#64748b', fontSize: '18px',
                    cursor: 'pointer', padding: '0 4px', lineHeight: 1, flexShrink: 0,
                  }}
                >&times;</button>
              </div>
            )}
            {page === 'app' && (
              <YouTubeAnalyzer buildUrl={buildUrl} user={user} isPro={user?.isPro || false} updateUser={updateUser} showCreditToast={showCreditToast} />
            )}
            {page === 'history' && (
              <HistoryPage buildUrl={buildUrl} user={user} isPro={user?.isPro || false} />
            )}
            {page === 'competitors' && (
              <CompetitorPage buildUrl={buildUrl} user={user} isPro={user?.isPro || false} />
            )}
            {page === 'simulator' && (
              <SimulatorPage user={user} updateUser={updateUser} showCreditToast={showCreditToast} />
            )}
            {page === 'payment' && (
              <PaymentPage user={user} onBack={goToApp} updateUser={updateUser} buildUrl={buildUrl} />
            )}
            {page === 'settings' && (
              <SettingsPage user={user} onUpgrade={goPayment} buildUrl={buildUrl} updateUser={updateUser} />
            )}
            {page === 'blog' && (
              <BlogPage onReadArticle={(slug) => navigate(`#/blog/${slug}`)} onGetStarted={() => navigate('#/')} />
            )}
            {page === 'article' && (
              <ArticlePage slug={getArticleSlug()} onBack={() => navigate('#/blog')} onGetStarted={() => navigate('#/')} />
            )}
          </div>
        </div>
        {creditToast !== null && <CreditToast amount={creditToast} />}
      </div>
    );
  }

  // ========== GUEST LAYOUT (Top navbar) ==========
  return (
    <div>
      <style>{APP_CSS}</style>
      <header style={headerStyles.header}>
        <span style={headerStyles.logo} onClick={() => navigate('#/')}>Titler</span>

        <div className="titler-nav-desktop" style={{ display: 'flex', alignItems: 'center' }}>
          <nav style={headerStyles.nav}>
            {GUEST_NAV.map(item => (
              <button
                key={item.key}
                onClick={() => navigate(item.hash)}
                style={page === item.key ? headerStyles.navLinkActive : headerStyles.navLink}
              >
                {item.label}
              </button>
            ))}
          </nav>
          <button onClick={goSignIn} style={headerStyles.signInBtn}>Sign in</button>
        </div>

        <button
          className="titler-hamburger"
          style={headerStyles.hamburger}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Menu"
        >
          {menuOpen ? (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          )}
        </button>
      </header>

      {menuOpen && (
        <div className="titler-mobile-menu" style={headerStyles.mobileMenu} ref={menuRef}>
          {GUEST_NAV.map(item => (
            <button
              key={item.key}
              onClick={() => navigate(item.hash)}
              style={page === item.key ? headerStyles.mobileNavLinkActive : headerStyles.mobileNavLink}
            >
              {item.label}
            </button>
          ))}
          <button onClick={goSignIn} style={{ ...headerStyles.mobileNavLink, color: '#c4b5fd', fontWeight: '600' }}>
            Sign in
          </button>
        </div>
      )}

      <div style={{ paddingTop: '56px' }}>
        {page === 'signin' && (
          <SignInPage showSignedOut={showSignedOut} onDismissSignedOut={() => setShowSignedOut(false)} />
        )}
        {page === 'app' && (
          <YouTubeAnalyzer buildUrl={buildUrl} user={user} isPro={false} updateUser={updateUser} showCreditToast={showCreditToast} />
        )}
        {page === 'landing' && (
          <LandingPage onGetStarted={goToApp} onSignIn={goSignIn} />
        )}
        {page === 'features' && (
          <FeaturesPage onGetStarted={goToApp} onSignIn={goSignIn} />
        )}
        {page === 'pricing' && (
          <PricingPage onFreeStart={goToApp} onProStart={handleProStart} />
        )}
        {page === 'blog' && (
          <BlogPage onReadArticle={(slug) => navigate(`#/blog/${slug}`)} onGetStarted={() => navigate('#/')} />
        )}
        {page === 'article' && (
          <ArticlePage slug={getArticleSlug()} onBack={() => navigate('#/blog')} onGetStarted={() => navigate('#/')} />
        )}
      {creditToast !== null && <CreditToast amount={creditToast} />}
      </div>
    </div>
  );
};

const CreditToast = ({ amount }) => (
  <div style={{
    position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
    background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(167, 139, 250, 0.3)',
    borderRadius: '12px', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)', animation: 'credit-toast-in 0.3s ease',
    backdropFilter: 'blur(12px)',
  }}>
    <style>{`
      @keyframes credit-toast-in {
        from { opacity: 0; transform: translateY(12px) scale(0.95); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
    `}</style>
    <svg viewBox="0 0 24 24" width="18" height="18" fill="#a78bfa">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
    </svg>
    <span style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '500' }}>
      {amount} credit{amount !== 1 ? 's' : ''} used
    </span>
  </div>
);

// --- Settings Page ---
const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

const SettingsPage = ({ user, onUpgrade, buildUrl, updateUser }) => {
  const isPro = user?.isPro || false;
  const [portalLoading, setPortalLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);

  // Check URL for Stripe redirect result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('stripe');
    if (status === 'success') {
      setStripeStatus('success');
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  }, []);

  useEffect(() => {
    if (!user?.email || !buildUrl) return;
    const endpoint = buildUrl('/me');
    if (!endpoint) return;
    fetch(endpoint, {
      headers: {
        'Authorization': `Bearer ${API_TOKEN}`,
        'X-User-Email': user.email,
      },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && updateUser) {
          updateUser({
            ...(data.name ? { name: data.name } : {}),
            requestLimit: data.requestLimit,
            isPro: data.isPro,
            tier: data.tier,
            stripeSubscriptionStatus: data.stripeSubscriptionStatus,
            subscriptionCurrentPeriodEnd: data.subscriptionCurrentPeriodEnd,
            cancelAtPeriodEnd: data.cancelAtPeriodEnd,
            hasStripeCustomer: data.hasStripeCustomer,
          });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManageSubscription = async () => {
    if (!user?.email) return;
    setPortalLoading(true);
    try {
      const res = await fetch(buildUrl('/create-portal-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user.email,
        },
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        setPortalLoading(false);
      }
    } catch {
      setPortalLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: '600px', margin: '0 auto', padding: '48px 24px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      color: '#e2e8f0',
    }}>
      <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '32px', marginTop: 0 }}>Settings</h1>

      {stripeStatus === 'success' && (
        <div style={{
          padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
          background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)',
          textAlign: 'center',
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#22c55e' }}>
            Payment successful! Welcome to Pro.
          </span>
        </div>
      )}

      {/* Account section */}
      <div style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px', padding: '24px', marginBottom: '20px',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Account</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {user.picture && (
            <img src={user.picture} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%', border: '2px solid rgba(124,58,237,0.4)' }} referrerPolicy="no-referrer" />
          )}
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0' }}>{user.name}</div>
            {user.email && <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>{user.email}</div>}
          </div>
        </div>
      </div>

      {/* Plan section */}
      <div style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px', padding: '24px', marginBottom: '20px',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plan</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
            background: isPro ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.06)',
            color: isPro ? '#a78bfa' : '#94a3b8',
          }}>
            {isPro ? 'Pro' : 'Free'}
          </span>
          {isPro && user.hasStripeCustomer && (
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              style={{
                padding: '6px 14px', borderRadius: '8px', border: '1px solid rgba(124,58,237,0.3)',
                background: 'rgba(124,58,237,0.1)', color: '#a78bfa', fontSize: '12px',
                fontWeight: '600', cursor: portalLoading ? 'wait' : 'pointer', fontFamily: 'inherit',
              }}
            >
              {portalLoading ? 'Loading...' : 'Manage Subscription'}
            </button>
          )}
        </div>
        {isPro && user.subscriptionCurrentPeriodEnd && (
          <p style={{ margin: '12px 0 0', fontSize: '13px', color: (user.cancelAtPeriodEnd) ? '#fbbf24' : '#64748b' }}>
            {user.cancelAtPeriodEnd
              ? `Pro access ends: ${new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString()}`
              : `Renews: ${new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString()}`}
          </p>
        )}
        {!isPro && (
          <div style={{ marginTop: '12px' }}>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#64748b' }}>
              Upgrade to unlock AI-powered analysis, YouTube URL analysis, and saved history.
            </p>
            <button
              onClick={onUpgrade}
              style={{
                padding: '8px 20px', borderRadius: '8px', border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                color: '#fff', fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Upgrade to Pro — $15/mo
            </button>
          </div>
        )}
      </div>

      {/* Credits section */}
      <div style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px', padding: '24px', marginBottom: '20px',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Credits</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <span style={{
            fontSize: '32px', fontWeight: '700',
            color: (user.requestLimit ?? 0) > 0 ? '#a78bfa' : '#f87171',
          }}>
            {user.requestLimit ?? 0}
          </span>
          <span style={{ fontSize: '14px', color: '#94a3b8' }}>credits remaining</span>
        </div>
        <div style={{
          width: '100%', height: '6px', borderRadius: '3px',
          background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
        }}>
          <div style={{
            width: `${Math.min(((user.requestLimit ?? 0) / (isPro ? 100 : 10)) * 100, 100)}%`,
            height: '100%', borderRadius: '3px',
            background: (user.requestLimit ?? 0) > 0
              ? 'linear-gradient(135deg, #7c3aed, #6366f1)'
              : '#f87171',
            transition: 'width 0.3s',
          }} />
        </div>
        <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#64748b' }}>
          {(user.requestLimit ?? 0) > 0
            ? 'Each video analysis uses 1 credit.'
            : isPro ? 'You\'ve used all your credits for this period.' : 'You\'ve used all your credits. Upgrade to Pro for more.'}
        </p>
      </div>
    </div>
  );
};

const App = () => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  </GoogleOAuthProvider>
);

export default App;
